// client/src/pages/EditorUser.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import { useSearchParams } from 'react-router-dom';

export default function EditorUser() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchId, setSearchId] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({});
  const [message, setMessage] = useState('');
  const [mastercars, setMastercars] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  
  // Для студентов: оплата и практика
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [bookingPlace, setBookingPlace] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Данные для select'ов
  const [times, setTimes] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Для умной формы записи (с визуализацией занятых слотов)
  const [occupiedSlots, setOccupiedSlots] = useState([]);
  const [forceOverwrite, setForceOverwrite] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Для mastercar: not_wdays
  const [notWdays, setNotWdays] = useState([]);
  const [newNotWday, setNewNotWday] = useState('');
  
  // Состояния для удаления (ТРЁХЭТАПНОЕ)
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [userAgreed, setUserAgreed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [step, setStep] = useState(1);
  
  // Состояния для перезаписи слотов
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [conflictingBooking, setConflictingBooking] = useState(null);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [oldStudentData, setOldStudentData] = useState(null);
  const [loadingOldStudent, setLoadingOldStudent] = useState(false);
  
  // Состояния для списка пользователей (ученики + инструкторы)
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUsersList, setShowUsersList] = useState(true);
  
  // Состояния для поиска и фильтров
  const [searchName, setSearchName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [groupsList, setGroupsList] = useState([]);

  // Загружаем список инструкторов и учеников при загрузке компонента
  useEffect(() => {
    console.log('EditorUser: Компонент смонтирован');
    fetchMastercars();
    fetchUsersList();
    
    // Загрузка групп
    fetch('/api/groups')
      .then(r => r.json())
      .then(data => setGroupsList(data))
      .catch(err => {
        console.error('Ошибка загрузки групп:', err);
        setGroupsList([]);
      });

    // Загрузка времени и мест для select'ов
    Promise.all([
      fetch('/api/times').then(r => r.json()).catch(() => []),
      fetch('/api/locations').then(r => r.json()).catch(() => [])
    ]).then(([t, l]) => {
      setTimes(t);
      setLocations(l);
    });
  }, []);

  // 🔥 НОВОЕ: Обработка URL-параметров при переходе из календаря
  useEffect(() => {
    const studentId = searchParams.get('studentId');
    const date = searchParams.get('date');
    const time = searchParams.get('time');
    const place = searchParams.get('place');
    const instructorId = searchParams.get('instructorId');
    const mode = searchParams.get('mode');
  
    if (studentId && mode) {
      // Автоматически подставляем ID ученика в поле поиска
      setSearchId(studentId);
    
      // Задержка, чтобы searchId успел обновиться в state
      const timer1 = setTimeout(() => {
        handleSearch();
      
        // После загрузки ученика подставляем данные записи
        const timer2 = setTimeout(() => {
          if (date) setBookingDate(date);
          if (time) setBookingTime(time);
          if (place) setBookingPlace(place);
        
          // Открываем форму записи
          setShowBookingForm(true);
        
          // Для режима reassign включаем чекбокс перезаписи
          if (mode === 'reassign') {
            setForceOverwrite(true);
          }
        
          // Показываем уведомление
          const modeText = mode === 'reassign' ? 'переназначения' : 'записи';
          setMessage(`🔄 Режим ${modeText}. Проверьте данные и нажмите «Записать».`);
        
          // Очищаем URL-параметры (чтобы при обновлении страницы не срабатывало повторно)
          setSearchParams({});
        }, 500);
      
        return () => clearTimeout(timer2);
      }, 100);
    
      return () => clearTimeout(timer1);
    }
  }, [searchParams]);

  // Загрузка занятых слотов инструктора при изменении даты
  useEffect(() => {
    if (bookingDate && editUser?.instructor?.id) {
      setLoadingSlots(true);
      fetch(`/api/schedule/${editUser.instructor.id}?_=${Date.now()}`)
        .then(r => r.ok ? r.json() : [])
        .then(slots => {
          setOccupiedSlots(slots.filter(s => s.date === bookingDate));
        })
        .catch(() => setOccupiedSlots([]))
        .finally(() => setLoadingSlots(false));
    } else {
      setOccupiedSlots([]);
    }
  }, [bookingDate, editUser?.instructor?.id]);

  // Сброс времени и чекбокса при смене даты
  useEffect(() => {
    setBookingTime('');
    setForceOverwrite(false);
  }, [bookingDate]);

  // Загрузка списка учеников и инструкторов (НЕ staff)
  const fetchUsersList = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUsersList(data);
      } else {
        console.error('Ошибка загрузки списка пользователей');
      }
    } catch (err) {
      console.error('Ошибка при загрузке списка пользователей:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Поиск пользователей с фильтрацией
  const performSearch = async (name, group) => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (name) params.append('name', name);
      if (group && group !== 'all') params.append('group', group);
      
      const url = `/api/users/search?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUsersList(data);
      } else {
        console.error('Ошибка поиска пользователей');
      }
    } catch (err) {
      console.error('Ошибка при поиске:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchMastercars = async () => {
    console.log('EditorUser: Загружаем список инструкторов...');
    try {
      const timestamp = Date.now();
      const res = await fetch(`/api/mastercars?t=${timestamp}&_=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        cache: 'no-store'
      });
      
      console.log('EditorUser: Статус ответа:', res.status, res.statusText);
      
      if (res.status === 304) {
        console.warn('EditorUser: Браузер упорно кеширует! Делаем POST запрос...');
        const postRes = await fetch('/api/mastercars-no-cache', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ timestamp: Date.now() })
        });
        const postData = await postRes.json();
        setMastercars(postData);
        return;
      }
      
      const data = await res.json();
      
      if (res.ok) {
        setMastercars(data);
        console.log('EditorUser: Список инструкторов обновлен');
      } else {
        console.error('EditorUser: Ошибка от сервера:', data);
        setMessage('Ошибка загрузки списка инструкторов');
      }
    } catch (err) {
      console.error('EditorUser: Ошибка сети:', err);
      setMessage('Ошибка соединения с сервером');
      
      try {
        console.log('EditorUser: Пробуем альтернативный запрос...');
        const altRes = await fetch(`http://localhost:5000/api/mastercars?_=${Date.now()}`, {
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        const altData = await altRes.json();
        setMastercars(altData);
      } catch (altErr) {
        console.error('EditorUser: Альтернативный запрос тоже не удался:', altErr);
      }
    }
  };

  const checkDeletionStatus = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/user/deleted-status/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDeletionStatus(data);
      }
    } catch (err) {
      console.error('Ошибка проверки статуса удаления:', err);
    }
  };

  // Копирование ID в буфер обмена
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage(`✅ ID ${text} скопирован`);
      setTimeout(() => setMessage(''), 2000);
    }).catch(() => {
      setMessage('❌ Ошибка копирования');
    });
  };

  if (user?.status !== 'staff') {
    return <div className="text-center py-20 text-red-600 text-2xl">Доступ запрещён</div>;
  }

  const handleSearch = async () => {
    setShowUsersList(false);
    
    if (!searchId.trim()) {
      setShowUsersList(true);
      setMessage('Введите ID пользователя');
      return;
    }

    setMessage('');
    setEditUser(null);
    setNotWdays([]);
    setPhotoPreview('');
    setSelectedPhoto(null);
    setDeletionStatus(null);
    setDeleteConfirmed(false);
    setUserAgreed(false);
    setAccessCode('');
    setStep(1);

    try {
      const res = await fetch(`/api/student/${searchId}?_=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await res.json();
      if (res.ok) {
        setEditUser(data);
        setForm({
          fullName: data.fullName || '',
          phone: data.phone || '',
          email: data.email || '',
          address: data.address || '',
          group: data.group || '', 
          experience: data.experience || '',
          instructorId: data.instructor?.id || '',
          newPassword: ''
        });

        if (data.photoUrl) {
          setPhotoPreview(data.photoUrl);
        }

        await checkDeletionStatus(data.id);

        if (data.status === 'mastercar') {
          try {
            const nwRes = await fetch(`/api/not-wdays/${data.id}?_=${Date.now()}`);
            const nwData = await nwRes.json();
            if (Array.isArray(nwData)) {
              setNotWdays(nwData);
            } else {
              setNotWdays([]);
            }
          } catch (nwErr) {
            console.error('EditorUser: Ошибка загрузки not_wdays:', nwErr);
            setNotWdays([]);
          }
        }
      } else {
        setMessage(data.message || 'Пользователь не найден');
      }
    } catch (err) {
      setMessage('Ошибка сервера: ' + err.message);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage('');

    const formData = new FormData();

    Object.keys(form).forEach(key => {
      if (form[key] !== undefined && form[key] !== null && form[key] !== '') {
        formData.append(key, form[key]);
      }
    });

    if (selectedPhoto) {
      formData.append('photo', selectedPhoto);
    }

    try {
      const res = await fetch(`/api/update-student/${searchId}?_=${Date.now()}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await res.json();
      if (res.ok) {
        setMessage('Данные обновлены');
        setEditUser(data.user);
        
        if (data.user.photoUrl) {
          setPhotoPreview(data.user.photoUrl);
        }
        
        setSelectedPhoto(null);
        
        if (data.user.status === 'mastercar') {
          fetchMastercars();
        }
      } else {
        setMessage(data.message || 'Ошибка');
      }
    } catch (err) {
      setMessage('Ошибка сервера: ' + err.message);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setMessage('Файл слишком большой (макс. 5MB)');
        return;
      }
      
      setSelectedPhoto(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Оплата (только для student)
  const handleAddPayment = async (e) => {
    e.preventDefault();
    setMessage('');
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(`/api/add-payment/${searchId}?_=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ amount: Number(paymentAmount), date: today })
      });

      const data = await res.json();
      if (res.ok) {
        setMessage('Оплата добавлена');
        setEditUser(data.user);
        setShowPaymentForm(false);
        setPaymentAmount('');
      } else {
        setMessage(data.message || 'Ошибка');
      }
    } catch (err) {
      setMessage('Ошибка сервера');
    }
  };

  // Обработчик клика по слоту времени в умной форме
  const handleTimeSlotClick = async (time) => {
    const isOccupied = occupiedSlots.some(s => s.time === time);
    
    if (isOccupied && !forceOverwrite) {
      // Слот занят и чекбокс не включён — игнорируем клик
      return;
    }
    
    if (isOccupied && forceOverwrite) {
      // Слот занят, чекбокс включён — показываем модалку подтверждения
      const conflicting = occupiedSlots.find(s => s.time === time);
      setConflictingBooking(conflicting);
      setPendingBooking({ date: bookingDate, time, place: bookingPlace });
      setBookingTime(time); // предварительно выбираем время
      
      // Подгружаем данные старого ученика (ФИО + группа)
      setOldStudentData(null);
      setLoadingOldStudent(true);
      
      if (conflicting.studentId && conflicting.type !== 'dop') {
        // Это обычная запись — подгружаем данные ученика
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`/api/student/${conflicting.studentId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (res.ok) {
            const data = await res.json();
            setOldStudentData({
              fullName: data.fullName || conflicting.studentName || 'Неизвестно',
              group: data.group || 'Не указана',
              phone: data.phone || '—',
              type: 'regular'
            });
          } else {
            // Если не удалось загрузить — используем данные из слота
            setOldStudentData({
              fullName: conflicting.studentName || 'Неизвестно',
              group: '—',
              phone: '—',
              type: 'regular'
            });
          }
        } catch (err) {
          console.error('Ошибка загрузки данных старого ученика:', err);
          setOldStudentData({
            fullName: conflicting.studentName || 'Неизвестно',
            group: '—',
            phone: '—',
            type: 'regular'
          });
        }
      } else if (conflicting.type === 'dop') {
        // Это доп. запись — без studentId
        setOldStudentData({
          fullName: conflicting.studentName || 'Дополнительная запись',
          group: '—',
          phone: '—',
          type: 'dop'
        });
      } else {
        // Неизвестный тип
        setOldStudentData({
          fullName: conflicting.studentName || 'Неизвестно',
          group: '—',
          phone: '—',
          type: 'unknown'
        });
      }
      
      setLoadingOldStudent(false);
      setShowOverwriteModal(true);
      return;
    }
    
    // Свободный слот — просто выбираем
    setBookingTime(time);
  };

  // Практика (только для student)
  const handleAddBooking = async (force = false) => {
    setMessage('');
    setLoading(true);

    try {
      const res = await fetch(`/api/add-booking/${searchId}?_=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          date: bookingDate, 
          time: bookingTime, 
          place: bookingPlace,
          force: force // флаг принудительной записи для staff
        })
      });

      const data = await res.json();

      if (res.ok) {
        setMessage('✅ Запись добавлена');
        setEditUser(data.user);
        setShowBookingForm(false);
        setBookingDate('');
        setBookingTime('');
        setBookingPlace('');
        setShowOverwriteModal(false);
        setOccupiedSlots([]);
        setOldStudentData(null);
      } else {
        setMessage(data.message || '❌ Ошибка');
      }
    } catch (err) {
      setMessage('❌ Ошибка сервера');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (index) => {
    try {
      const res = await fetch(`/api/cancel-booking/${searchId}/${index}?_=${Date.now()}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        setMessage('Запись отменена');
        setEditUser(data.user);
      }
    } catch (err) {
      setMessage('Ошибка');
    }
  };

  // not_wdays (для mastercar)
  const handleAddNotWday = () => {
    console.log('Добавление даты:', newNotWday);
    console.log('Текущие даты:', notWdays);
    
    if (newNotWday && !notWdays.includes(newNotWday)) {
      const updated = [...notWdays, newNotWday];
      console.log('Новые даты:', updated);
      setNotWdays(updated);
      setNewNotWday('');
    } else {
      console.log('Дата не добавлена (уже есть или пустая)');
    }
  };

  const handleRemoveNotWday = (index) => {
    setNotWdays(notWdays.filter((_, i) => i !== index));
  };

  const handleSaveNotWdays = async () => {
    console.log('=== КЛИЕНТ: Начало сохранения not_wdays ===');
    console.log('ID инструктора:', editUser?.id);
    console.log('Даты для сохранения:', notWdays);
    
    if (!editUser || editUser.status !== 'mastercar') {
      console.log('Ошибка: не инструктор или editUser не найден');
      setMessage('Только для инструкторов');
      return;
    }

    try {
      const url = `/api/not-wdays/${editUser.id}?_=${Date.now()}`;
      console.log('URL запроса:', url);
      
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ dates: notWdays })
      });

      console.log('Статус ответа:', res.status);
      
      const data = await res.json();
      console.log('Данные ответа:', data);
      
      if (res.ok) {
        console.log('Успешно сохранено');
        setMessage('Исключения сохранены');
      } else {
        console.log('Ошибка от сервера:', data.message);
        setMessage(data.message || 'Ошибка сохранения');
      }
    } catch (err) {
      console.error('Ошибка сети:', err);
      setMessage('Ошибка сети: ' + err.message);
    }
    console.log('=== КЛИЕНТ: Конец сохранения not_wdays ===');
  };

  const handleSoftDelete = async () => {
    if (accessCode !== 'SuRe$192837465$26$08') {
      setMessage('❌ Неверный код доступа');
      return;
    }
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/user/soft-delete/${editUser.id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setMessage('✅ Пользователь помечен для удаления через 14 дней');
        setShowDeleteModal(false);
        setDeleteConfirmed(false);
        setUserAgreed(false);
        setAccessCode('');
        setStep(1);
        checkDeletionStatus(editUser.id);
      } else {
        setMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      setMessage('❌ Ошибка сервера');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async () => {
    if (!window.confirm('Восстановить пользователя?')) return;
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/user/restore/${editUser.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setMessage('✅ Пользователь восстановлен');
        checkDeletionStatus(editUser.id);
      } else {
        setMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      setMessage('❌ Ошибка сервера');
    } finally {
      setDeleting(false);
    }
  };

  // Сброс модального окна
  const resetDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmed(false);
    setUserAgreed(false);
    setAccessCode('');
    setStep(1);
  };

  return (
    <div className="min-h-screen pb-20 bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-center mb-8">Редактирование пользователя</h2>

          {/* Поиск */}
          <div className="flex gap-4 mb-8">
            <input
              type="text"
              placeholder="ID пользователя"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
            />
            <button
              onClick={handleSearch}
              className="bg-blue-600 text-white px-8 py-3 rounded-full font-medium hover:bg-blue-700 transition"
            >
              Найти
            </button>
          </div>

          {/* СПИСОК ПОЛЬЗОВАТЕЛЕЙ (УЧЕНИКИ + ИНСТРУКТОРЫ) */}
          {showUsersList && (
            <div className="mb-8">
              <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Ученики и инструкторы</h3>
                <div className="flex gap-2">
                  <button
                    onClick={fetchUsersList}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                    title="Обновить список"
                  >
                    <span>🔄</span> Обновить
                  </button>
                </div>
              </div>
            
              {/* ПОЛОСА ПОИСКА И ФИЛЬТРОВ */}
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex-1 min-w-[200px]">
                  <input
                    type="text"
                    placeholder="Поиск по ФИО..."
                    value={searchName}
                    onChange={(e) => {
                      setSearchName(e.target.value);
                      performSearch(e.target.value, selectedGroup);
                    }}
                    className="w-full px-4 py-2 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                  />
                </div>
                
                <div className="relative">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition flex items-center gap-2"
                  >
                    <span>🔧</span> Фильтры
                    <svg className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* ВЫПАДАЮЩЕЕ ОКНО ФИЛЬТРОВ */}
                  {showFilters && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-xl border z-10 p-4">
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Группа</label>
                        <select
                          value={selectedGroup}
                          onChange={(e) => {
                            setSelectedGroup(e.target.value);
                            performSearch(searchName, e.target.value);
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                        >
                          <option value="all">Все группы</option>
                          {groupsList.map(group => (
                            <option key={group.id} value={group.id}>{group.name}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={() => {
                          setSearchName('');
                          setSelectedGroup('all');
                          performSearch('', 'all');
                        }}
                        className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
                      >
                        Сбросить фильтры
                      </button>
                    </div>
                  )}
                </div>
              </div>
            
              {loadingUsers ? (
                <div className="text-center py-8">
                  <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-gray-700 mt-2">Загрузка списка...</p>
                </div>
              ) : usersList.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <p className="text-gray-700">Нет зарегистрированных пользователей</p>
                </div>
              ) : (
                <div className="border rounded-xl overflow-hidden">
                  <div className="grid grid-cols-12 bg-gray-100 font-medium p-3 text-sm text-gray-900">
                    <div className="col-span-2">ID</div>
                    <div className="col-span-5">ФИО</div>
                    <div className="col-span-2">Группа</div>
                    <div className="col-span-1">Тип</div>
                    <div className="col-span-2 text-center">Действие</div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {usersList.map((user, index) => (
                      <div 
                        key={user.id}
                        className={`grid grid-cols-12 p-3 text-sm hover:bg-gray-100 border-t text-gray-800 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <div className="col-span-2 font-mono text-blue-700 text-xs truncate" title={user.id}>
                          {user.id}
                        </div>
                        <div className="col-span-5 truncate font-medium" title={user.fullName}>
                          {user.fullName}
                        </div>
                        <div className="col-span-2 text-sm text-gray-600 truncate">
                          {user.status === 'student' ? (user.group || '—') : '—'}
                        </div>
                        <div className={`col-span-1 font-medium ${
                          user.status === 'student' ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {user.status === 'student' ? '👨‍🎓' : '👨‍🏫'}
                        </div>
                        <div className="col-span-2 flex justify-center gap-2">
                          <button
                            onClick={() => copyToClipboard(user.id)}
                            className="text-blue-700 hover:text-blue-900 px-2 py-1 rounded hover:bg-blue-100 transition"
                            title="Копировать ID"
                          >
                            📋
                          </button>
                          <button
                            onClick={() => {
                              setSearchId(user.id);
                              handleSearch();
                            }}
                            className="text-green-700 hover:text-green-900 px-2 py-1 rounded hover:bg-green-100 transition"
                            title="Найти этого пользователя"
                          >
                            🔍
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-gray-100 p-3 text-sm text-gray-700 border-t">
                    Всего: {usersList.length} (учеников: {usersList.filter(u => u.status === 'student').length}, инструкторов: {usersList.filter(u => u.status === 'mastercar').length})
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Уведомление о статусе удаления */}
          {deletionStatus?.isDeleted && editUser && (
            <div className="mb-6 p-4 bg-red-100 border-2 border-red-300 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-3xl">⚠️</span>
                <div>
                  <h3 className="font-bold text-red-700">Аккаунт помечен для удаления</h3>
                  <p className="text-sm text-red-600">
                    Будет полностью удалён через {deletionStatus.daysLeft} дней
                  </p>
                </div>
              </div>
            </div>
          )}

          {editUser && (
            <>
              {/* Базовая форма (для всех) */}
              <form onSubmit={handleUpdate} className="space-y-6 mb-12">
                <div className="flex justify-between items-center">
                  <h3 className="text-2xl font-semibold">Основные данные</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    editUser.status === 'student' ? 'bg-green-100 text-green-800' :
                    editUser.status === 'mastercar' ? 'bg-blue-100 text-blue-800' :
                    editUser.status === 'staff' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {editUser.status === 'student' ? 'Студент' : 
                     editUser.status === 'mastercar' ? 'Инструктор' : 
                     editUser.status === 'staff' ? 'Администратор' : editUser.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="ФИО"
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                  />
                  <input
                    type="tel"
                    placeholder="Телефон"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                  />
                  <input
                    type="text"
                    placeholder="Адрес"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                  />
                </div>

                {/* Поле для студента: группа */}
                {editUser.status === 'student' && (
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Группа:</label>
                    <select
                      value={form.group}
                      onChange={(e) => setForm({ ...form, group: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                    >
                      <option value="">Не выбрана</option>
                      {groupsList.length === 0 ? (
                        <option value="" disabled>Загрузка групп...</option>
                      ) : (
                        groupsList.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Выберите группу из списка
                    </p>
                  </div>
                )}

                {/* Поле для инструктора: стаж */}
                {editUser.status === 'mastercar' && (
                  <input
                    type="number"
                    placeholder="Стаж (лет)"
                    value={form.experience}
                    onChange={(e) => setForm({ ...form, experience: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                    min="0"
                    max="50"
                  />
                )}

                {/* Поле для студента: выбор инструктора */}
                {editUser.status === 'student' && (
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Инструктор:</label>
                    <select
                      value={form.instructorId || ''}
                      onChange={(e) => setForm({ ...form, instructorId: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                    >
                      <option value="">Не назначен</option>
                      {mastercars.length === 0 ? (
                        <option value="" disabled>Загрузка инструкторов...</option>
                      ) : (
                        mastercars.map((mastercar) => (
                          <option key={mastercar.id} value={mastercar.id}>
                            {mastercar.fullName} 
                            {mastercar.experience && mastercar.experience !== '0' ? 
                              ` (стаж: ${mastercar.experience} ${mastercar.experience === '1' ? 'год' : 
                                ['2', '3', '4'].includes(mastercar.experience) ? 'года' : 'лет'})` : ''}
                          </option>
                        ))
                      )}
                    </select>
                    <div className="mt-2 text-sm">
                      {mastercars.length === 0 ? (
                        <p className="text-yellow-600">
                          Список инструкторов пуст. Проверьте консоль браузера (F12) на наличие ошибок.
                        </p>
                      ) : (
                        <p className="text-gray-600">
                          Доступно инструкторов: {mastercars.length}
                        </p>
                      )}
                      {editUser.instructor?.fullName && (
                        <p className="text-gray-600 mt-1">
                          Текущий инструктор: <strong>{editUser.instructor.fullName}</strong>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Поле для инструктора: загрузка фото */}
                {editUser.status === 'mastercar' && (
                  <div>
                    <label className="block mb-2 font-medium text-gray-700">Фото инструктора:</label>
                    <div className="flex items-center gap-4">
                      {photoPreview && (
                        <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-300">
                          <img 
                            src={photoPreview} 
                            alt="Превью" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoChange}
                          className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:text-sm file:font-semibold
                            file:bg-blue-50 file:text-blue-700
                            hover:file:bg-blue-100"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Только изображения (макс. 5MB)
                        </p>
                        {!photoPreview && editUser.photoUrl && (
                          <p className="text-sm text-gray-500 mt-1">
                            Текущее фото: <a href={editUser.photoUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">посмотреть</a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <input
                  type="password"
                  placeholder="Новый пароль (опционально)"
                  value={form.newPassword || ''}
                  onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                />

                <button
                  type="submit"
                  disabled={deletionStatus?.isDeleted}
                  className="w-full bg-blue-600 text-white py-4 rounded-full font-bold text-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Сохранить данные
                </button>
              </form>

              {/* Контент только для student */}
              {editUser.status === 'student' && (
                <>
                  {/* Оплата */}
                  <div className="mb-12">
                    <h3 className="text-2xl font-semibold mb-4">Оплата обучения</h3>
                    <button
                      onClick={() => setShowPaymentForm(!showPaymentForm)}
                      disabled={deletionStatus?.isDeleted}
                      className="bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      Добавить оплату
                    </button>

                    {showPaymentForm && (
                      <form onSubmit={handleAddPayment} className="space-y-4 mt-4">
                        <input
                          type="number"
                          placeholder="Сумма оплаты"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                          required
                          min="0"
                          step="100"
                        />
                        <p className="text-gray-600">Дата оплаты будет сегодняшняя автоматически</p>
                        <button
                          type="submit"
                          className="w-full bg-green-600 text-white py-4 rounded-full font-bold text-lg hover:bg-green-700 transition"
                        >
                          Добавить оплату
                        </button>
                      </form>
                    )}

                    <h4 className="text-xl font-medium mt-6 mb-2">История оплат</h4>
                    {(editUser.payments || []).length === 0 ? (
                      <p className="text-gray-500">Оплат пока нет</p>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="grid grid-cols-3 bg-gray-100 font-medium p-3">
                          <div>Дата</div>
                          <div>Сумма</div>
                          <div>Всего оплачено</div>
                        </div>
                        {editUser.payments.map((p, i) => (
                          <div key={i} className="grid grid-cols-3 border-t p-3 hover:bg-gray-50">
                            <div>{p.date}</div>
                            <div className="font-medium">{p.amount} ₽</div>
                            <div className="text-gray-600">
                              {i === editUser.payments.length - 1 ? (
                                <span className="font-bold text-green-600">{editUser.payment || 0} ₽</span>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Практика с умной формой записи */}
                  <div>
                    <h3 className="text-2xl font-semibold mb-4">Запись на практику</h3>
                    <button
                      onClick={() => setShowBookingForm(!showBookingForm)}
                      disabled={deletionStatus?.isDeleted}
                      className="bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      {showBookingForm ? 'Скрыть форму' : 'Записать на практику'}
                    </button>

                    {showBookingForm && (
                      <div className="space-y-5 mt-4 p-5 bg-gray-50 rounded-2xl border border-gray-200">
                        {/* Дата */}
                        <div>
                          <label className="block mb-2 font-medium text-gray-700">Дата занятия</label>
                          <input
                            type="date"
                            value={bookingDate}
                            onChange={(e) => setBookingDate(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                            required
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </div>

                        {/* Время — сетка слотов */}
                        {bookingDate && (
                          <div>
                            <label className="block mb-2 font-medium text-gray-700">
                              Время {loadingSlots && <span className="text-sm text-gray-500">(загрузка...)</span>}
                            </label>
                            
                            {!editUser?.instructor?.id ? (
                              <p className="text-yellow-600 text-sm p-3 bg-yellow-50 rounded-lg">
                                ⚠️ У ученика не назначен инструктор. Назначьте инструктора в основных данных выше.
                              </p>
                            ) : times.length === 0 ? (
                              <p className="text-gray-500">Время не загружено</p>
                            ) : (
                              <>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                                  {times.map((t) => {
                                    const isOccupied = occupiedSlots.some(s => s.time === t);
                                    const isSelected = bookingTime === t;
                                    const canClick = !isOccupied || forceOverwrite;
                                    
                                    let btnClass = 'px-3 py-2.5 rounded-lg text-sm font-medium transition border ';
                                    
                                    if (isSelected) {
                                      btnClass += 'bg-blue-600 text-white border-blue-600 ring-2 ring-blue-300';
                                    } else if (isOccupied) {
                                      if (forceOverwrite) {
                                        btnClass += 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200 cursor-pointer';
                                      } else {
                                        btnClass += 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed';
                                      }
                                    } else {
                                      btnClass += 'bg-white text-gray-800 border-gray-300 hover:bg-blue-50 hover:border-blue-400 cursor-pointer';
                                    }
                                    
                                    return (
                                      <button
                                        key={t}
                                        type="button"
                                        onClick={() => handleTimeSlotClick(t)}
                                        disabled={!canClick}
                                        className={btnClass}
                                        title={isOccupied ? (forceOverwrite ? 'Занято — кликните для перезаписи' : 'Занято') : 'Свободно'}
                                      >
                                        {t}
                                        {isOccupied && <span className="ml-1 text-xs">🔒</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                                
                                {/* Легенда */}
                                <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-2">
                                  <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 bg-white border border-gray-300 rounded"></span>
                                    Свободно
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 bg-gray-200 rounded"></span>
                                    Занято
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 bg-orange-100 border border-orange-300 rounded"></span>
                                    Перезапись
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-3 h-3 bg-blue-600 rounded"></span>
                                    Выбрано
                                  </span>
                                </div>
                                
                                <p className="text-xs text-gray-500">
                                  Свободно: {times.length - occupiedSlots.length} из {times.length}
                                </p>
                              </>
                            )}
                          </div>
                        )}

                        {/* Место */}
                        <div>
                          <label className="block mb-2 font-medium text-gray-700">Место</label>
                          <select
                            value={bookingPlace}
                            onChange={(e) => setBookingPlace(e.target.value)}
                            className="w-full px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                            required
                          >
                            <option value="">Выберите место</option>
                            {locations.length > 0 ? (
                              locations.map((loc, i) => (
                                <option key={i} value={loc}>{loc}</option>
                              ))
                            ) : (
                              <option value="" disabled>Загрузка мест...</option>
                            )}
                          </select>
                        </div>

                        {/* Чекбокс перезаписи — ВСЕГДА виден */}
                        <label className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-xl cursor-pointer hover:bg-yellow-100 transition">
                          <input
                            type="checkbox"
                            checked={forceOverwrite}
                            onChange={(e) => setForceOverwrite(e.target.checked)}
                            className="w-5 h-5 mt-0.5 accent-yellow-600"
                          />
                          <div className="flex-1">
                            <span className="font-medium text-gray-800">Разрешить перезапись занятых слотов</span>
                            <p className="text-xs text-gray-600 mt-1">
                              Если включено — можно выбирать занятые слоты (оранжевые). Старая запись будет отменена.
                            </p>
                          </div>
                        </label>

                        {/* Кнопка записи */}
                        <button
                          onClick={() => handleAddBooking(false)}
                          disabled={loading || !bookingDate || !bookingTime || !bookingPlace || !editUser?.instructor?.id}
                          className="w-full bg-green-600 text-white py-4 rounded-full font-bold text-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Запись...' : 'Записать'}
                        </button>
                      </div>
                    )}

                    <h4 className="text-xl font-medium mt-6 mb-2">Текущие записи на практику</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="p-3 text-left">Дата</th>
                            <th className="p-3 text-left">Время</th>
                            <th className="p-3 text-left">Адрес</th>
                            <th className="p-3 text-left">Действия</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(editUser.booking || []).length === 0 ? (
                            <tr>
                              <td colSpan="4" className="text-center p-6 text-gray-500">
                                Записей нет
                              </td>
                            </tr>
                          ) : (
                            editUser.booking.map((b, i) => (
                              <tr key={i} className="border-t hover:bg-gray-50">
                                <td className="p-3">{b.date}</td>
                                <td className="p-3">{b.time}</td>
                                <td className="p-3">{b.place}</td>
                                <td className="p-3">
                                  <button
                                    onClick={() => handleCancelBooking(i)}
                                    disabled={deletionStatus?.isDeleted}
                                    className="bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm hover:bg-red-200 transition disabled:opacity-50"
                                  >
                                    Отменить
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {/* Контент только для mastercar */}
              {editUser.status === 'mastercar' && (
                <div className="mt-12">
                  <h3 className="text-2xl font-semibold mb-4">
                    Редактирование исключений для инструктора: {editUser.fullName}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Добавьте даты, когда инструктор не работает. Эти даты будут исключены из расписания практики.
                  </p>

                  <div className="flex gap-4 mb-4">
                    <input
                      type="date"
                      value={newNotWday}
                      onChange={(e) => setNewNotWday(e.target.value)}
                      className="flex-1 px-4 py-3 border rounded-lg focus:border-blue-600 focus:outline-none text-gray-900"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <button
                      onClick={handleAddNotWday}
                      className="bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition"
                    >
                      Добавить
                    </button>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-medium mb-3">Текущие исключения ({notWdays.length})</h4>
                    {notWdays.length === 0 ? (
                      <p className="text-gray-500 p-4 bg-gray-50 rounded-lg">Исключений нет</p>
                    ) : (
                      <ul className="space-y-2">
                        {notWdays.map((date, i) => (
                          <li key={i} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg hover:bg-gray-100">
                            <span className="font-medium">{date}</span>
                            <button
                              onClick={() => handleRemoveNotWday(i)}
                              className="text-red-600 hover:text-red-800 font-medium text-sm px-3 py-1 hover:bg-red-50 rounded"
                            >
                              Удалить
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <button
                    onClick={handleSaveNotWdays}
                    className="w-full bg-green-600 text-white py-4 rounded-full font-bold text-lg hover:bg-green-700 transition"
                    disabled={notWdays.length === 0}
                  >
                    {notWdays.length === 0 ? 'Нет изменений для сохранения' : 'Сохранить исключения'}
                  </button>
                </div>
              )}

              {/* СЕКЦИЯ УДАЛЕНИЯ - В САМОМ НИЗУ */}
              <div className="mt-16 pt-8 border-t-2 border-gray-200">
                <h3 className="text-2xl font-semibold mb-4 text-red-600">Опасная зона</h3>
                
                {deletionStatus?.isDeleted ? (
                  <button
                    onClick={handleRestore}
                    disabled={deleting}
                    className="w-full bg-green-600 text-white py-4 rounded-full font-bold text-lg hover:bg-green-700 transition disabled:opacity-50"
                  >
                    {deleting ? 'Обработка...' : 'Восстановить аккаунт'}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="w-full bg-red-600 text-white py-4 rounded-full font-bold text-lg hover:bg-red-700 transition"
                  >
                    Удалить аккаунт
                  </button>
                )}
              </div>
            </>
          )}

          {message && (
            <div className={`text-center mt-8 p-4 rounded-lg ${
              message.includes('успешно') || 
              message.includes('обновлены') || 
              message.includes('добавлена') ||  
              message.includes('отменена') || 
              message.includes('сохранены') ||
              message.includes('восстановлен') ||
              message.includes('помечен') ||
              message.includes('скопирован') ? 
              'bg-green-50 text-green-700 border border-green-200' : 
              'bg-red-50 text-red-700 border border-red-200'
            }`}>
              <p className="text-xl font-medium">{message}</p>
            </div>
          )}
        </div>
      </div>

      {/* МОДАЛЬНОЕ ОКНО ПОДТВЕРЖДЕНИЯ УДАЛЕНИЯ (ТРЁХЭТАПНОЕ) */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl">
            <h2 className="text-2xl font-black mb-4 text-red-600">⚠️ Подтверждение удаления</h2>
            
            {/* ЭТАП 1: Два чекбокса */}
            {step === 1 && (
              <>
                <p className="text-gray-600 mb-6">
                  Аккаунт будет помечен для удаления и полностью удалён через 14 дней.
                  Все будущие записи будут отменены.
                </p>
                
                <div className="space-y-4 mb-6">
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <input
                      type="checkbox"
                      checked={deleteConfirmed}
                      onChange={(e) => setDeleteConfirmed(e.target.checked)}
                      className="w-5 h-5"
                    />
                    <span className="text-gray-700">Я подтверждаю удаление аккаунта</span>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <input
                      type="checkbox"
                      checked={userAgreed}
                      onChange={(e) => setUserAgreed(e.target.checked)}
                      className="w-5 h-5"
                    />
                    <span className="text-gray-700">Пользователь согласен на удаление</span>
                  </label>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={resetDeleteModal}
                    className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={() => {
                      if (deleteConfirmed && userAgreed) {
                        setStep(2);
                      }
                    }}
                    disabled={!deleteConfirmed || !userAgreed}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    Далее
                  </button>
                </div>
              </>
            )}

            {/* ЭТАП 2: Код доступа */}
            {step === 2 && (
              <>
                <p className="text-gray-600 mb-4">
                  Введите код доступа для подтверждения операции
                </p>
                
                <input
                  type="password"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="*************"
                  className="w-full px-4 py-3 border-2 rounded-xl mb-4 text-center text-2xl tracking-widest"
                  autoFocus
                />
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setStep(1);
                      setAccessCode('');
                    }}
                    className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition"
                  >
                    Назад
                  </button>
                  <button
                    onClick={handleSoftDelete}
                    disabled={accessCode !== 'SuRe$192837465$26$08' || deleting}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 transition"
                  >
                    {deleting ? 'Удаление...' : 'Подтвердить удаление'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* МОДАЛЬНОЕ ОКНО ПЕРЕЗАПИСИ СЛОТА (с ФИО, группой и телефоном) */}
      {showOverwriteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl">
            <h2 className="text-2xl font-black mb-4 text-yellow-600">⚠️ Слот занят</h2>
            
            <p className="text-gray-600 mb-4">
              Вы хотите записать ученика <strong className="text-blue-600">{editUser?.fullName}</strong> на:
            </p>

            <div className="bg-blue-50 p-4 rounded-xl mb-4 border border-blue-200">
              <p className="mb-1"><span className="font-medium">Дата:</span> {pendingBooking?.date}</p>
              <p className="mb-1"><span className="font-medium">Время:</span> {pendingBooking?.time}</p>
              <p><span className="font-medium">Место:</span> {pendingBooking?.place}</p>
            </div>

            <p className="text-gray-600 mb-4">
              Это время уже занято. Будет отменена запись:
            </p>
            
            <div className="bg-red-50 p-4 rounded-xl mb-6 border border-red-200">
              {loadingOldStudent ? (
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-600">Загрузка данных...</span>
                </div>
              ) : oldStudentData ? (
                <>
                  {/* Бейдж типа записи */}
                  <div className="flex items-center gap-2 mb-3">
                    {oldStudentData.type === 'dop' ? (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">
                        📝 Доп. запись
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                        👨‍🎓 Ученик
                      </span>
                    )}
                  </div>
                  
                  {/* ФИО */}
                  <div className="mb-2">
                    <span className="text-sm text-gray-500">ФИО:</span>
                    <p className="font-bold text-gray-900 text-lg">{oldStudentData.fullName}</p>
                  </div>
                  
                  {/* Группа (только для обычных учеников) */}
                  {oldStudentData.type === 'regular' && (
                    <div className="mb-2">
                      <span className="text-sm text-gray-500">Группа:</span>
                      <p className="font-medium text-gray-800">{oldStudentData.group}</p>
                    </div>
                  )}
                  
                  {/* Телефон (только для обычных учеников) */}
                  {oldStudentData.type === 'regular' && oldStudentData.phone !== '—' && (
                    <div className="mb-2">
                      <span className="text-sm text-gray-500">Телефон:</span>
                      <p className="font-medium text-gray-800">{oldStudentData.phone}</p>
                    </div>
                  )}
                  
                  {/* Детали слота */}
                  <div className="mt-3 pt-3 border-t border-red-200">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Дата:</span> {conflictingBooking?.date}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Время:</span> {conflictingBooking?.time}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Место:</span> {conflictingBooking?.place}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-gray-600">Не удалось загрузить данные</p>
              )}
            </div>
            
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6">
              <p className="text-yellow-800 font-bold text-sm">
                ❗ Предупредите ученика об отмене занятия!
              </p>
              <p className="text-yellow-700 text-xs mt-1">
                Запись будет удалена без возможности восстановления.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowOverwriteModal(false);
                  setBookingTime('');
                  setOldStudentData(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition"
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  setShowOverwriteModal(false);
                  setOldStudentData(null);
                  handleAddBooking(true); // true = принудительная запись
                }}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition"
              >
                Перезаписать
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}